import * as XLSX from 'xlsx';

/**
 * Generate Excel template for individual clients
 * @returns {Blob} Excel file blob
 */
export const generateIndividualClientTemplate = () => {
  const headers = [
    'name',
    'id/passport number', 
    'email',
    'phone',
    'address',
    'tax number',
    'date of birth'
  ];

  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Individual Clients');
  
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

/**
 * Generate Excel template for entity clients
 * @returns {Blob} Excel file blob
 */
export const generateEntityClientTemplate = () => {
  const headers = [
    'name',
    'legal name',
    'email',
    'phone',
    'registration number',
    'license number',
    'address'
  ];

  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Entity Clients');
  
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

/**
 * Parse CSV/TSV content and return array of client objects
 * @param {string} content - CSV/TSV content
 * @param {string} delimiter - Delimiter (',' for CSV, '\t' for TSV)
 * @returns {Array} Array of client objects
 */
const parseCSVContent = (content, delimiter = ',') => {
  const lines = content.split('\n').filter(line => line.trim());
  
  if (lines.length < 2) {
    throw new Error('File must contain at least a header row and one data row');
  }
  
  const headers = lines[0].split(delimiter).map(h => h.trim());
  const rows = lines.slice(1);
  
  // Filter out empty rows - only include rows that have at least one non-empty cell
  const nonEmptyRows = rows.filter(row => {
    const values = row.split(delimiter);
    return values.some(value => value.trim() !== '');
  });
  
  
  if (nonEmptyRows.length === 0) {
    throw new Error('No data rows found in the file');
  }
  
  return nonEmptyRows.map((row, index) => {
    const client = {};
    const values = row.split(delimiter);
    
    headers.forEach((header, colIndex) => {
      if (header) {
        const value = values[colIndex] || '';
        const fieldName = header.toLowerCase().replace(/\s+/g, '');
        // Check for commas in non-address fields
        if (header.toLowerCase() !== 'address' && value.includes(',') && value.trim() !== '') {
          throw new Error(`Commas are not allowed in the "${header}" field. Please use a different separator or remove commas from the data.`);
        }
        client[fieldName] = value.trim();
      }
    });
    client._rowIndex = index + 2; // +2 because of header and 0-based index
    return client;
  });
};

/**
 * Parse Excel file and return array of client objects
 * @param {File} file - Excel/CSV/TSV file
 * @param {string} type - 'individual' or 'entity'
 * @returns {Promise<Array>} Array of client objects
 */
export const parseExcelFile = async (file, type) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const fileName = file.name.toLowerCase();
        
        // Handle CSV files
        if (fileName.endsWith('.csv')) {
          const content = e.target.result;
          const clients = parseCSVContent(content, ',');
          resolve(clients);
          return;
        }
        
        // Handle TSV files
        if (fileName.endsWith('.tsv')) {
          const content = e.target.result;
          const clients = parseCSVContent(content, '\t');
          resolve(clients);
          return;
        }
        
        // Handle Excel files (.xlsx, .xls)
        if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            reject(new Error('File must contain at least a header row and one data row'));
            return;
          }
          
          const headers = jsonData[0];
          // Filter out empty rows - only include rows that have at least one non-empty cell
          const rows = jsonData.slice(1).filter(row => {
            return row.some(cell => cell !== undefined && cell !== null && cell.toString().trim() !== '');
          });
          
          
          if (rows.length === 0) {
            reject(new Error('No data rows found in the file'));
            return;
          }
          
          const clients = rows.map((row, index) => {
            const client = {};
            headers.forEach((header, colIndex) => {
              if (header) {
                const value = row[colIndex] || '';
                const fieldName = header.toLowerCase().replace(/\s+/g, '');
                // Check for commas in non-address fields for Excel files too
                if (header.toLowerCase() !== 'address' && value.toString().includes(',') && value.toString().trim() !== '') {
                  throw new Error(`Commas are not allowed in the "${header}" field. Please use a different separator or remove commas from the data.`);
                }
                client[fieldName] = value.toString().trim();
              }
            });
            client._rowIndex = index + 2; // +2 because of header and 0-based index
            return client;
          });
          
          resolve(clients);
          return;
        }
        
        reject(new Error('Unsupported file format. Please use CSV, TSV, or Excel files (.xlsx, .xls)'));
      } catch (error) {
        reject(new Error(`Failed to parse file: ${error.message}`));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    
    // Use text reader for CSV/TSV, array buffer for Excel
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.csv') || fileName.endsWith('.tsv')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
};

/**
 * Validate client data before submission
 * @param {Array} data - Array of client objects
 * @param {string} type - 'individual' or 'entity'
 * @returns {Object} Validation result with errors
 */
export const validateClientData = (data, type) => {
  const errors = [];
  const validClients = [];
  
  data.forEach((client, index) => {
    const clientErrors = [];
    const rowNumber = client._rowIndex || index + 2;
    
    if (type === 'individual') {
      // Only name is required
      if (!client.name || client.name.trim() === '') {
        clientErrors.push(`Row ${rowNumber}: Name is required`);
      }
      
    } else if (type === 'entity') {
      // Only name is required
      if (!client.name || client.name.trim() === '') {
        clientErrors.push(`Row ${rowNumber}: Name is required`);
      }
    }
    
    if (clientErrors.length > 0) {
      errors.push({
        row: rowNumber,
        client: client,
        errors: clientErrors
      });
    } else {
      validClients.push(client);
    }
  });
  
  return {
    validClients,
    errors,
    hasErrors: errors.length > 0
  };
};

/**
 * Download Excel file
 * @param {Blob} blob - File blob
 * @param {string} filename - Filename for download
 */
export const downloadExcelFile = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
